import { useState, useEffect, useCallback } from 'react';
import { useApolloClient, useMutation, useQuery } from 'react-apollo';
import { useHistory } from 'react-router-dom';
import moment from 'moment';
import { useDispatch } from 'react-redux';

import useOpenAlert from '../../../../hooks/useOpenAlert';
import { getErrorString } from '../../../../util/normalize-errors';
import {
  accountEmailExists as accountEmailExistsQuery,
  MeQuery,
} from '../../../../graphql/queries/account';
import { gtm } from '../../../../util/analytics';
import {
  accountUpdate as accountUpdateMutation,
  accountCreateByEmail as accountCreateByEmailMutation,
} from '../../../../graphql/mutations/account';
import { STEPS } from '../constants';
import { loginSuccess } from '../../../../actions/auth-actions';
import marketingAccountLinkMutation from '../mutations/marketing-account-link';
import { albumForJoinTypeCheck } from '../queries/album';
import { ALBUM_FOLDER_JOIN_TYPE } from '../../../../constants/album-folder';
import ACCOUNT_ROLES from '../../../../constants/account-roles';
import usePhoneAuth from '../../../../components/auth/hooks/usePhoneAuth';
import useGoogleRecaptcha from '../../../../services/recaptcha/useGoogleRecaptcha';

const useMarketingAccountMatches = ({
  marketingAccountId,
  sourceEmailAddress,
  albumId,
}) => {
  const dispatch = useDispatch();
  const history = useHistory();
  const [step, setStep] = useState(STEPS.USER_INFORMATION);
  const [loading, setLoading] = useState(false);
  const [
    checkingEmailVerificationStepRequirements,
    setCheckingEmailVerificationStepRequirements,
  ] = useState(!!sourceEmailAddress);
  const [showEmailVerificationStep, setShowEmailVerificationStep] = useState(
    !!sourceEmailAddress,
  );
  const client = useApolloClient();
  const openAlert = useOpenAlert();
  const onLogin = useCallback(() => dispatch(loginSuccess()));
  const { onTokenRequest } = useGoogleRecaptcha();
  const {
    loading: phoneAuthLoading,
    pinSent,
    requireHumanInput,
    blockLogin,
    onPhoneAuthSubmit,
    onPhoneAuthVerify,
    onResendPhoneAuthCode,
  } = usePhoneAuth();

  const { data: { album } = {} } = useQuery(albumForJoinTypeCheck, {
    variables: {
      id: albumId,
    },
  });

  const [accountCreateByEmail] = useMutation(accountCreateByEmailMutation);
  const [accountUpdate] = useMutation(accountUpdateMutation);
  const [marketingAccountLink] = useMutation(marketingAccountLinkMutation);

  const checkEmailAddressAvailability = async emailAddress => {
    const res = await client.query({
      query: accountEmailExistsQuery,
      variables: {
        email_address: emailAddress,
      },
    });

    return res.data.accountEmailExists.exists;
  };

  const checkSourceEmailAddressAvailability = async () => {
    const emailExists = await checkEmailAddressAvailability(sourceEmailAddress);

    if (emailExists || !sourceEmailAddress) {
      setShowEmailVerificationStep(false);
    }

    setCheckingEmailVerificationStepRequirements(false);
  };

  useEffect(() => {
    if (sourceEmailAddress) {
      checkSourceEmailAddressAvailability();
    }
  }, [sourceEmailAddress]);

  useEffect(() => {
    if (pinSent && step !== STEPS.PIN_VERIFICATION) {
      setStep(STEPS.PIN_VERIFICATION);
    }
  }, [pinSent]);

  const onPhoneVerificationRequest = async values => {
    if (loading) {
      return;
    }

    try {
      setLoading(true);
      const token = await onTokenRequest({ action: 'login' });
      await onPhoneAuthSubmit(
        values.phone_number,
        true,
        token,
        values.force_allow_action,
      );
    } catch (responseError) {
      openAlert({
        message: 'There was an error with your registration',
        info: responseError?.request_id ?? getErrorString(responseError),
      });
    } finally {
      setLoading(false);
    }
  };

  const onPinCodeResend = async values => {
    if (loading) {
      return;
    }

    try {
      setLoading(true);
      const token = await onTokenRequest({ action: 'login' });
      await onResendPhoneAuthCode(
        values.phone_number,
        true,
        token,
        values.force_allow_action,
      );
    } catch (responseError) {
      openAlert({
        message: 'There was an error sending the PIN.',
        info: responseError?.request_id ?? getErrorString(responseError),
      });
    } finally {
      setLoading(false);
    }
  };

  const onSignIn = async () => {
    try {
      if (!showEmailVerificationStep) {
        await marketingAccountLink({
          variables: {
            input: {
              marketing_account_id: marketingAccountId,
            },
          },
        });

        const entityId =
          album?.album_folder?.picture_day_enabled ||
          album?.album_folder?.join_type === ALBUM_FOLDER_JOIN_TYPE.ALL
            ? album?.album_folder?.id ?? albumId
            : albumId;

        history.push(`/app/shutterbug/hierarchy-items/${entityId}/matches`);
      } else {
        setShowEmailVerificationStep(false);
        setStep(STEPS.EMAIL_VERIFICATION);
      }
    } catch (e) {
      openAlert({
        message: 'There was an error linking identities',
        info: getErrorString(e),
      });
    }
  };

  const onEmailRegistrationRequest = async values => {
    if (loading) {
      return;
    }

    setLoading(true);
    const matchesUrl = `/marketing-account-matches/redirect?accountId=${marketingAccountId}&albumId=${albumId}`;

    try {
      const emailExists = await checkEmailAddressAvailability(
        values.email_address,
      );

      if (emailExists) {
        throw new Error('Email address already in use.');
      }

      await accountCreateByEmail({
        variables: {
          input: {
            email: values.email_address,
            name: `${values.first_name} ${values.last_name}`.trim(),
            password: values.password,
            email_verification_redirect: matchesUrl,
            roles: [ACCOUNT_ROLES.SHUTTERBUG.toUpperCase()],
          },
        },
      });

      // Create Marketing Account Link.
      // This ensures the user can access their matches regardless of whether they use
      // the 1st verification email we sent, or requests a new one.
      await marketingAccountLink({
        variables: {
          input: {
            marketing_account_id: marketingAccountId,
          },
        },
      });

      setShowEmailVerificationStep(false);
      setStep(STEPS.EMAIL_VERIFICATION_SENT);
    } catch (e) {
      openAlert(getErrorString(e));
    } finally {
      setLoading(false);
    }
  };

  const onEmailVerificationRequest = async values => {
    if (!values.should_verify_email_address) {
      await onSignIn();
      return;
    }

    const emailExists = await checkEmailAddressAvailability(
      values.email_address,
    );

    if (emailExists) {
      openAlert({
        message: 'There was an error with your registration',
        info:
          'This email address is already registered to a different account!',
      });
    }

    const { data: { me } = {} } = await client.query({
      query: MeQuery,
      fetchPolicy: 'network-only',
    });

    if (me) {
      await accountUpdate({
        variables: {
          input: {
            id: me?.id,
            email_address: values.email_address,
            skip_email_verification: true,
          },
        },
      });
    }

    await onSignIn();
  };

  const onPinVerify = async values => {
    if (loading) {
      return;
    }

    try {
      setLoading(true);
      await onPhoneAuthVerify(values.phone_number, values.code);
      await onLogin();

      const { data: { me } = {} } = await client.query({
        query: MeQuery,
        fetchPolicy: 'network-only',
      });

      await accountUpdate({
        variables: {
          input: {
            id: me.id,
            name: `${values.first_name} ${values.last_name}`.trim(),
            state_id: values.state_id ?? undefined,
            client_store_items: [
              {
                key: 'shutterbug_signup_timestamp',
                value: moment().valueOf().toString(),
              },
            ],
          },
        },
      });

      gtm.trackEvent({
        type: 'MarketingAccountMatchesRegistration',
        action: null,
      });

      await onSignIn();
    } catch (responseError) {
      openAlert({
        message: 'There was an error with your registration',
        info: responseError?.request_id ?? getErrorString(responseError),
      });
    } finally {
      setLoading(false);
    }
  };

  return {
    step,
    setStep,
    loading: loading || phoneAuthLoading,
    checkingEmailVerificationStepRequirements,
    showEmailVerificationStep,
    onPhoneVerificationRequest,
    onEmailRegistrationRequest,
    onEmailVerificationRequest,
    onPinCodeResend,
    onPinVerify,
    onSignIn,
    requireHumanInput,
    blockLogin,
  };
};

export default useMarketingAccountMatches;
